import React from "react";
import { Link } from "react-router-dom";
import { Info } from "lucide-react";
import { DD_MM_YYYY } from "src/helpers";
import { ROUTES_CONFIG } from "src/constants/routesConfig";
import { TransferOrderColumnHeader } from "src/enums";
import { TransferOrder, TableColumn } from "src/types";
import { StatusPill } from "src/components/ui";
import { Table } from "src/components";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "src/shadcn/ui/tooltip";

interface TransferOrdersDashTableProps {
  transferOrders: TransferOrder[];
  columnsToHide?: TransferOrderColumnHeader[];
}

const TransferOrdersDashTable: React.FC<TransferOrdersDashTableProps> = ({ transferOrders, columnsToHide }) => {
  const transferOrdersTableColumns: TableColumn<TransferOrderColumnHeader, TransferOrder>[] = [
    {
      header: TransferOrderColumnHeader.TRANSFER_ORDER,
      column: (row: TransferOrder) => (
        <Link
          to={`${ROUTES_CONFIG.transferOrder.path}?id=${row.id}`}
          className="text-primary-900 hover:text-primary-700 hover:underline transition-colors whitespace-nowrap"
        >
          {row.id}
        </Link>
      ),
      sticky: true
    },
    {
      header: TransferOrderColumnHeader.COMPLETION_STATUS,
      column: (row: TransferOrder) => (
        <span className="flex justify-center">
          {row.completionStatus && <StatusPill status={row.completionStatus} />}
        </span>
      ),
      styles: { textAlign: "center", minWidth: "140px" }
    },
    {
      header: TransferOrderColumnHeader.PRODUCTS_COUNT,
      column: (row: TransferOrder) => <span className="text-center block">{row.products?.length ?? 0}</span>,
      styles: { textAlign: "center", minWidth: "100px" }
    },
    {
      header: TransferOrderColumnHeader.CREATED_BY,
      column: (row: TransferOrder) => <span className="whitespace-nowrap">{row.createdByEmail ?? "-"}</span>,
      styles: { minWidth: "180px" }
    },
    {
      header: TransferOrderColumnHeader.CREATION_DATE,
      column: (row: TransferOrder) => (
        <span className="whitespace-nowrap">{row.createdAt ? DD_MM_YYYY.formatDate(row.createdAt) : "-"}</span>
      ),
      styles: { minWidth: "120px" }
    },
    {
      header: TransferOrderColumnHeader.ACTIONS,
      column: (row: TransferOrder) => (
        <TooltipProvider>
          <div className="flex items-center justify-center gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Link
                  to={`${ROUTES_CONFIG.transferOrder.path}?id=${row.id}`}
                  className="inline-flex items-center justify-center h-8 w-8 p-0 rounded-md hover:bg-secondary-100 transition-colors"
                >
                  <Info className="h-4 w-4 text-muted-600" />
                </Link>
              </TooltipTrigger>
              <TooltipContent>
                <p>More Info</p>
              </TooltipContent>
            </Tooltip>
          </div>
        </TooltipProvider>
      ),
      styles: { textAlign: "center" }
    }
  ];

  return (
    <Table<TransferOrderColumnHeader, TransferOrder>
      data={transferOrders}
      columns={transferOrdersTableColumns}
      columnsToHide={columnsToHide}
    />
  );
};

export { TransferOrdersDashTable };
